import { type Attributes, type Span, type Tracer, trace } from "@opentelemetry/api";
import { flatten } from "flat";
import type {
  TimingEvent,
  TimingSpan,
  TimingSpanWithEndTime,
  TimingSpanWithoutEndTime,
  TimingStrategy,
} from "../types";

/**
 * Strategy implementation for otel timing.
 */
export class OtelTimingStrategy implements TimingStrategy {
  private readonly tracer: Tracer;
  private readonly spansById: Map<string, Span> = new Map();

  public constructor() {
    this.tracer = trace.getTracer("OtelTimingStrategy");
  }

  public startSpan(span: TimingSpanWithoutEndTime): void {
    const otelSpan = this.tracer.startSpan(span.name, {
      startTime: this.toDate(span.startTime),
      attributes: this.attributesFrom(span),
    });

    this.spansById.set(span.id, otelSpan);
  }

  public endSpan(span: TimingSpanWithEndTime): void {
    const otelSpan = this.spansById.get(span.id);

    if (otelSpan) {
      otelSpan.setAttributes(this.attributesFrom(span));

      const endTime = this.toDate(span.endTime ?? performance.now());
      otelSpan.end(endTime);
      this.spansById.delete(span.id);
      return;
    }

    this.createSpan(span);
  }

  public createSpan(span: TimingSpan): void {
    const otelSpan = this.tracer.startSpan(span.name, {
      startTime: this.toDate(span.startTime),
      attributes: this.attributesFrom(span),
    });

    const resolvedEnd = this.toDate(span.endTime ?? performance.now());
    otelSpan.end(resolvedEnd);
  }

  /**
   * Pretend to create a nested span but actually only create a flat one.
   *
   * OTEL doesn't support nested spans in the browser because browsers don't have a mechanism for reliably tracking
   * contexts across async calls.
   *
   * As a result, this function will only ever create flat spans, despite calling Tracer#startActiveSpan to create
   * nested ones.
   *
   * Node provides the AsyncLocalStorage API for this purpose [1], and OTEL's Node implementation uses it [2].
   *
   * But in the browser, there's currently only a proposal to introduce a similar API [3]. Angular made an attempt to
   * solve this problem with a library called Zone.js [4], which monkey-patches the Promise API to allow it to keep
   * track of the current execution context.
   *
   * But in 2025, Zone.js is a non-starter because it's incompatible with the ES6 async/await pattern (which cannot be
   * monkey-patched [5]). Angular has since moved to a "zoneless" model to avoid this problem [6].
   *
   * OTEL does provide a ZoneContextManager [7] but this relies on the underling Zone.js library and is therefore
   * incompatible with our ES6 codebase. Experimentation shows that it works *occasionally* but not reliably enough to
   * bother with.
   *
   * [1] https://nodejs.org/api/async_context.html
   * [2] https://github.com/open-telemetry/opentelemetry-js/blob/v2.0.1/packages/opentelemetry-context-async-hooks/src/AsyncLocalStorageContextManager.ts
   * [3] https://github.com/tc39/proposal-async-context
   * [4] https://github.com/angular/zone.js
   * [5] https://github.com/angular/angular/issues/31730#issuecomment-2027358130
   * [6] https://angular.dev/guide/zoneless
   * [7] https://www.npmjs.com/package/@opentelemetry/context-zone
   *
   * @param span
   * @param callback
   */
  // biome-ignore lint/suspicious/noExplicitAny: Need to match ReturnType<F>, which uses any
  public async withSpan<F extends () => Promise<any>>(
    span: TimingSpanWithoutEndTime,
    callback: F,
  ): Promise<Awaited<ReturnType<F>>> {
    return await this.tracer.startActiveSpan(
      span.name,
      {
        startTime: this.toDate(span.startTime),
        attributes: this.attributesFrom(span),
      },
      async (otelSpan) => {
        const response = await callback();
        otelSpan.end();
        return response;
      },
    );
  }

  public createEvent(event: TimingEvent): void {
    const otelSpan = this.tracer.startSpan(event.name, {
      startTime: this.toDate(event.startTime),
      attributes: this.attributesFrom(event),
    });

    const endAt = this.toDate(event.startTime);
    otelSpan.end(endAt);
  }

  public reset(): void {
    for (const [, otelSpan] of this.spansById) {
      otelSpan.end();
    }
    this.spansById.clear();
  }

  private toDate(ts: DOMHighResTimeStamp): Date {
    return new Date(performance.timeOrigin + ts);
  }

  private attributesFrom(spanOrEvent: TimingSpan | TimingEvent): Attributes {
    const { telemetryCategory, ...details } = spanOrEvent.details ?? {};
    const attributes = flatten(details) as unknown as Attributes;
    if (telemetryCategory) {
      attributes["telemetry.category"] = telemetryCategory;
    }
    return attributes;
  }
}
